// Always-on patient interaction timer (wpc-front port) — the engine lives here
// because the drawer unmounts on close. Elapsed is timestamp-based (hidden-tab
// interval throttling can't under-count); system-suspend gaps are spliced out,
// never billed. Full billing invariants: docs/specs/2026-07-15-patient-drawer-time-tracking.md.

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import InteractionService from '@/services/InteractionService';
// Sanctioned lib import: the definitive-vs-ambiguous retry policy hinges on
// WsError's "server answered" semantics — a service re-export would force the
// test mocks to carry the class.
import { WsError } from '@/lib/wiservice';
import { queryClient } from '@/state/data/_shared/queryClient';
import { INTERACTION_KEYS } from '@/state/data/interaction/keys';
import {
  FLUSH_CHUNK_SECONDS,
  FLUSH_INTERVAL_MS,
  MAX_CONTINUOUS_SESSION_SECONDS,
  SUSPEND_GAP_MS,
  computeRecoverableSeconds,
  isClaimableCacheEntry,
  roundSecondsWithThreshold,
  timerStartsPausedFor,
  ymd,
  type TimerCacheEntry,
} from '@/logic/timeTracking';

const CACHE_KEY = 'rpm_timer_cache';
const TICK_MS = 5_000;
// Identifies THIS tab's cache entries so another tab's recovery sweep never
// claims a live session (it only claims entries with a stale lastTickAt).
const TAB_ID = Math.random().toString(36).slice(2) + Date.now().toString(36);

let heartbeatId: ReturnType<typeof setInterval> | null = null;
let tickerId: ReturnType<typeof setInterval> | null = null;

// ── localStorage cache (crash recovery) — opaque withings_user_id only, no PHI ──

type TimerCache = Record<string, TimerCacheEntry>;

function readCache(): TimerCache {
  try {
    const raw = window.localStorage.getItem(CACHE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
}

function writeCache(cache: TimerCache): void {
  try {
    if (Object.keys(cache).length === 0) window.localStorage.removeItem(CACHE_KEY);
    else window.localStorage.setItem(CACHE_KEY, JSON.stringify(cache));
  } catch { /* storage unavailable — recovery degrades, timer still works */ }
}

function removeCacheEntry(userid: string): void {
  const cache = readCache();
  if (userid in cache) {
    delete cache[userid];
    writeCache(cache);
  }
}

// ── Flush plumbing ──────────────────────────────────────────────────────────

interface FlushPayload {
  userid: string;
  programid?: string;
  authorid?: string;
  seconds: number;
  startedAtMs: number;
}

type FlushResult = 'ok' | 'skipped' | 'definitive-fail' | 'ambiguous-fail';

async function postAutoTime(p: FlushPayload): Promise<FlushResult> {
  const rounded = roundSecondsWithThreshold(p.seconds);
  if (rounded === false) return 'skipped';
  try {
    await InteractionService.createTimeEntry({
      userid: p.userid,
      timeSeconds: rounded,
      dateYmd: ymd(new Date(p.startedAtMs)),
      authorid: p.authorid,
      autoStartDateUnix: Math.floor(p.startedAtMs / 1000),
      programid: p.programid,
    });
    // The store bypasses TanStack — without this, a reopened drawer reads the
    // pre-flush list/seed from cache until staleTime expires.
    queryClient.invalidateQueries({ queryKey: INTERACTION_KEYS.all });
    return 'ok';
  } catch (e) {
    // WsError = the server answered with an error → the write did NOT land →
    // safe to retry. Anything else (timeout/network) is ambiguous → drop.
    return e instanceof WsError ? 'definitive-fail' : 'ambiguous-fail';
  }
}

// Claim-then-POST: stale entries are synchronously deleted from the cache
// BEFORE any await, so StrictMode's double start() can't double-bill.
function sweepRecovery(): void {
  const now = Date.now();
  const cache = readCache();
  const claimed: Array<[string, TimerCacheEntry]> = [];
  Object.entries(cache).forEach(([uid, entry]) => {
    if (isClaimableCacheEntry(entry, TAB_ID, now)) {
      claimed.push([uid, entry]);
      delete cache[uid];
    }
  });
  if (claimed.length === 0) return;
  writeCache(cache);
  claimed.forEach(([uid, entry]) => {
    const seconds = computeRecoverableSeconds(entry, now);
    if (seconds <= 0) return;
    postAutoTime({
      userid: uid,
      programid: entry.programid,
      authorid: entry.authorid,
      seconds,
      startedAtMs: entry.startedAt,
    }).then((result) => {
      if (result === 'definitive-fail') {
        // Re-park for a later sweep — the write definitively didn't land.
        const c = readCache();
        if (!(uid in c)) {
          c[uid] = { ...entry };
          writeCache(c);
        }
      } else if (result === 'ambiguous-fail') {
        console.warn('[timer] recovery flush dropped (ambiguous failure)', { userid: uid, seconds });
      }
    });
  });
}

// ── Store ───────────────────────────────────────────────────────────────────

export interface TimeTrackingState {
  /** Patient the timer is running for (String()-normalized), or null. */
  userid: string | null;
  /** Snapshotted at start() — the unmount flush runs after the URL changed. */
  programid?: string;
  authorid?: string;
  /** First start of this session (ms) — the auto_start_date anchor. */
  startedAt: number | null;
  /** Last resume (ms); null while paused. */
  runningSince: number | null;
  /** Seconds frozen by pause() or spliced around a suspend gap. */
  accumulated: number;
  /** Seconds already POSTed (or deliberately dropped) this session. */
  flushedSeconds: number;
  /** Last moment this tab was observably alive — the suspend-gap detector. */
  lastSeenAt: number | null;
  /** True once this session wrote its cache entry (claim detection). */
  stamped: boolean;
  paused: boolean;
  autoPaused: boolean;
  /** Paused because this author's timer is opt-in, not because a human paused it. */
  defaultPaused: boolean;

  start: (userid: string | number, attribution?: { programid?: string; authorid?: string }) => void;
  stop: (userid: string | number) => void;
  pause: () => void;
  resume: () => void;
  setAttribution: (attribution: { programid?: string; authorid?: string }) => void;
}

function elapsedSecondsOf(s: Pick<TimeTrackingState, 'accumulated' | 'runningSince'>, now: number): number {
  return s.accumulated + (s.runningSince ? Math.max(0, (now - s.runningSince) / 1000) : 0);
}

const RESET = {
  userid: null,
  programid: undefined,
  authorid: undefined,
  startedAt: null,
  runningSince: null,
  accumulated: 0,
  flushedSeconds: 0,
  lastSeenAt: null,
  stamped: false,
  paused: false,
  autoPaused: false,
  defaultPaused: false,
} as const;

const useTimeTrackingStore = create<TimeTrackingState>()(
  devtools((set, get) => {
    // A gap since the last observed tick means the machine slept (timers don't
    // run during system suspend) — splice it out: nobody was working.
    function reconcileClock(now: number): void {
      const s = get();
      if (!s.userid) return;
      if (s.lastSeenAt != null && now - s.lastSeenAt > SUSPEND_GAP_MS && s.runningSince != null) {
        const upTo = Math.max(s.runningSince, s.lastSeenAt);
        set({
          accumulated: s.accumulated + Math.max(0, (upTo - s.runningSince) / 1000),
          runningSince: now,
          lastSeenAt: now,
        });
        console.warn('[timer] suspend gap spliced (not billed)', {
          userid: s.userid,
          gapSeconds: Math.round((now - s.lastSeenAt) / 1000),
        });
        return;
      }
      set({ lastSeenAt: now });
    }

    function stampCache(now: number): void {
      const s = get();
      if (!s.userid || s.startedAt == null) return;
      const cache = readCache();
      if (s.stamped && !(s.userid in cache)) {
        // Our entry vanished mid-session → another tab's recovery claimed it
        // and billed our unflushed tail. Forfeit it (under-bill, never double-bill).
        const total = Math.floor(elapsedSecondsOf(s, now));
        if (total > s.flushedSeconds) {
          set({ flushedSeconds: total });
          console.warn('[timer] session claimed by another tab — unflushed tail forfeited', { userid: s.userid });
        }
      }
      const st = get();
      if (!st.userid || st.startedAt == null) return;
      cache[st.userid] = {
        tabId: TAB_ID,
        startedAt: st.startedAt,
        elapsedSeconds: Math.floor(elapsedSecondsOf(st, now)),
        flushedSeconds: st.flushedSeconds,
        lastTickAt: now,
        programid: st.programid,
        authorid: st.authorid,
      };
      writeCache(cache);
      if (!st.stamped) set({ stamped: true });
    }

    function clearIntervals(): void {
      if (heartbeatId != null) { clearInterval(heartbeatId); heartbeatId = null; }
      if (tickerId != null) { clearInterval(tickerId); tickerId = null; }
    }

    function heartbeat(): void {
      const now = Date.now();
      reconcileClock(now);
      const s = get();
      if (!s.userid || s.startedAt == null) return;
      // Belt on top of the splice: never flush past the auto-pause cap.
      const total = Math.min(elapsedSecondsOf(s, now), MAX_CONTINUOUS_SESSION_SECONDS);
      const pending = total - s.flushedSeconds;
      const chunks = Math.floor(pending / FLUSH_CHUNK_SECONDS);
      if (chunks > 0) {
        const seconds = chunks * FLUSH_CHUNK_SECONDS;
        const payload: FlushPayload = {
          userid: s.userid,
          programid: s.programid,
          authorid: s.authorid,
          seconds,
          startedAtMs: s.startedAt,
        };
        // Advance optimistically; roll back only on a DEFINITIVE server error
        // (retry later) and only for THIS session (startedAt-keyed) — an
        // ambiguous failure stays advanced = dropped.
        set({ flushedSeconds: s.flushedSeconds + seconds });
        postAutoTime(payload).then((result) => {
          if (result === 'definitive-fail') {
            set((st) => (st.userid === payload.userid && st.startedAt === payload.startedAtMs
              ? { flushedSeconds: Math.max(0, st.flushedSeconds - seconds) }
              : {}));
          } else if (result === 'ambiguous-fail') {
            console.warn('[timer] auto flush dropped (ambiguous failure)', { userid: payload.userid, seconds });
          }
        });
      }
      if (!s.paused && total >= MAX_CONTINUOUS_SESSION_SECONDS) {
        // Runaway-session guard: freeze at the cap; one click resumes.
        set({
          accumulated: MAX_CONTINUOUS_SESSION_SECONDS,
          runningSince: null,
          paused: true,
          autoPaused: true,
        });
        stampCache(now);
      }
    }

    return {
      ...RESET,

      start: (userid, attribution) => {
        const uid = String(userid);
        const s = get();
        if (s.userid === uid) {
          // StrictMode remount / late-arriving attribution — never reset the clock.
          get().setAttribution(attribution ?? {});
          return;
        }
        if (s.userid) get().stop(s.userid);
        sweepRecovery();
        const now = Date.now();
        // Opt-in authors start frozen at 0 — the heartbeat/stop flushes read
        // elapsed off runningSince, so a null one bills nothing until play.
        const startsPaused = timerStartsPausedFor(attribution?.authorid);
        set({
          ...RESET,
          userid: uid,
          programid: attribution?.programid,
          authorid: attribution?.authorid,
          startedAt: now,
          runningSince: startsPaused ? null : now,
          lastSeenAt: now,
          paused: startsPaused,
          defaultPaused: startsPaused,
        });
        stampCache(now);
        clearIntervals();
        heartbeatId = setInterval(heartbeat, FLUSH_INTERVAL_MS);
        tickerId = setInterval(() => {
          const tickNow = Date.now();
          reconcileClock(tickNow);
          stampCache(tickNow);
        }, TICK_MS);
      },

      stop: (userid) => {
        const uid = String(userid);
        const s0 = get();
        if (!s0.userid || s0.userid !== uid || s0.startedAt == null) return;
        clearIntervals();
        const now = Date.now();
        reconcileClock(now);
        const s = get();
        const total = Math.min(elapsedSecondsOf(s, now), MAX_CONTINUOUS_SESSION_SECONDS);
        const payload: FlushPayload = {
          userid: uid,
          programid: s.programid,
          authorid: s.authorid,
          seconds: total - s.flushedSeconds,
          startedAtMs: s.startedAt ?? now,
        };
        const parkEntry: TimerCacheEntry = {
          tabId: 'parked',
          startedAt: s.startedAt ?? now,
          elapsedSeconds: Math.floor(total),
          flushedSeconds: s.flushedSeconds,
          lastTickAt: now,
          programid: s.programid,
          authorid: s.authorid,
        };
        removeCacheEntry(uid);
        set({ ...RESET });
        postAutoTime(payload).then((result) => {
          if (result === 'definitive-fail') {
            // Park the tail under its own entry — recovered by a later sweep.
            const cache = readCache();
            if (!(uid in cache)) {
              cache[uid] = parkEntry;
              writeCache(cache);
            }
          } else if (result === 'ambiguous-fail') {
            console.warn('[timer] stop flush dropped (ambiguous failure)', { userid: uid, seconds: payload.seconds });
          }
        });
      },

      pause: () => {
        const now = Date.now();
        reconcileClock(now);
        const s = get();
        if (!s.userid || s.paused) return;
        // Freeze — never flush-or-discard here (a sub-minute tail must survive).
        set({
          accumulated: elapsedSecondsOf(s, now), runningSince: null, paused: true, autoPaused: false,
        });
        stampCache(now);
      },

      resume: () => {
        const s = get();
        if (!s.userid || !s.paused) return;
        const now = Date.now();
        set({
          runningSince: now, lastSeenAt: now, paused: false, autoPaused: false, defaultPaused: false,
        });
      },

      setAttribution: (attribution) => {
        const s = get();
        if (!s.userid) return;
        const next: Partial<TimeTrackingState> = {};
        if (attribution.programid && !s.programid) next.programid = attribution.programid;
        if (attribution.authorid && attribution.authorid !== s.authorid) {
          next.authorid = attribution.authorid;
          // authorid can arrive after mount (account fetch), so the opt-in check
          // runs again here — the only path that can miss it. Keyed on "the id
          // CHANGED", which makes it one-shot per session: a deliberate play
          // press is never undone by a repeat attribution with the same id.
          // The head accrued before the id was known is DISCARDED, not frozen: a
          // slow account fetch would otherwise ride along into the first flush
          // after a play press and over-bill it.
          if (timerStartsPausedFor(attribution.authorid) && !s.paused) {
            next.accumulated = 0;
            next.runningSince = null;
            next.paused = true;
            next.autoPaused = false;
            next.defaultPaused = true;
          }
        }
        if (Object.keys(next).length > 0) set(next);
      },
    };
  }, { name: 'useTimeTrackingStore' }),
);

// Crashed-tab entries are also recovered at app boot (not only on the next
// drawer open). Delayed past wsInit(); skipped in vitest (tests seed the cache).
if (typeof window !== 'undefined' && import.meta.env.MODE !== 'test') {
  window.setTimeout(sweepRecovery, 15_000);
}

// Vite HMR re-evaluates the module and would orphan the running intervals.
if (import.meta.hot) {
  import.meta.hot.dispose(() => {
    if (heartbeatId != null) clearInterval(heartbeatId);
    if (tickerId != null) clearInterval(tickerId);
  });
}

// ── Consumer surface ────────────────────────────────────────────────────────
// Stable-field selectors only. Elapsed time is NOT a selector (a Date.now()-
// computing selector breaks the cached-getSnapshot contract) — the chip runs
// its own 1 s interval over getTimerSnapshot().

export const useTimerUserid = () => useTimeTrackingStore((s) => s.userid);
export const useTimerPaused = () => useTimeTrackingStore((s) => s.paused);
export const useTimerAutoPaused = () => useTimeTrackingStore((s) => s.autoPaused);
export const useTimerDefaultPaused = () => useTimeTrackingStore((s) => s.defaultPaused);

export interface TimerSnapshot {
  userid: string | null;
  paused: boolean;
  autoPaused: boolean;
  elapsedSeconds: number;
  flushedSeconds: number;
  /** Live not-yet-persisted seconds — what the chip adds to the server seed. */
  unflushedSeconds: number;
}

export function getTimerSnapshot(now: number = Date.now()): TimerSnapshot {
  const s = useTimeTrackingStore.getState();
  const elapsed = s.userid ? elapsedSecondsOf(s, now) : 0;
  return {
    userid: s.userid,
    paused: s.paused,
    autoPaused: s.autoPaused,
    elapsedSeconds: elapsed,
    flushedSeconds: s.flushedSeconds,
    unflushedSeconds: Math.max(0, elapsed - s.flushedSeconds),
  };
}

export default useTimeTrackingStore;
